import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { detectUserTimezone } from '@rocco/ui/calendar/utils/timezone-helpers';
import type { CalendarAvailabilityData } from '../api/get-calendar-availability.server';
import { convertFreeDaysToTimezone } from '../utils/convert-timezone';
import { useTimezoneSync, VALID_TIMEZONES } from '../utils/timezone-cookie';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface BookingActionData {
    ok: boolean;
    message: string;
    eventId?: string;
    isReschedule?: boolean;
    clientDirectorEmail?: string;
}

export interface BookingDetails {
    clientEmail: string;
    clientName: string;
    clientCompany: string;
    organizerId?: number | null;
}

export interface UseBookingScheduleInput {
    /** Availability for the session being booked (from loader or the availability resource route). */
    availability: CalendarAvailabilityData | undefined;
    /** Timezone used for the initial render (URL param > cookie > availability > default). */
    serverTimezone: string;
    /** Set when the timezone was pinned via a URL param; disables browser auto-detection. */
    requestTimezoneFromUrl?: string;
    isReschedule?: boolean;
    originalEventId?: string;
    bookingDetails?: BookingDetails;
    /**
     * Endpoint the booking form posts to. Defaults to the current route (the
     * standalone /booking page); the in-page modal points this at the booking
     * resource route.
     */
    actionPath?: string;
    /**
     * Optional key for the submit fetcher. The in-page modal varies this per
     * open so the booking lifecycle (including a previous success) resets each
     * time the modal is reopened.
     */
    fetcherKey?: string;
    /**
     * Opt into the round-robin person picker (BC-4603). When false (the default)
     * round-robin availability stays pooled — `selectedMemberId` is never set,
     * `freeDays` is unfiltered and submit auto-assigns the first available
     * member. Only the inline project-detail booking card, which renders the
     * picker, sets this; the standalone /booking page and the Client Director
     * modal leave it off so they keep showing every consultant's slots.
     */
    enableMemberSelection?: boolean;
}

/**
 * Get the raw browser timezone from Intl API.
 * Falls back to detectUserTimezone() for environments without Intl support.
 * If both fail (e.g. Intl unavailable), returns Pacific/Auckland as a safe default.
 */
function getRawBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        try {
            return detectUserTimezone();
        } catch {
            return 'Pacific/Auckland';
        }
    }
}

/**
 * Owns the entire booking orchestration — timezone/duration/slot selection and
 * the submit lifecycle — so it can drive both the standalone /booking page and
 * the in-page Client Director booking modal. Consumers render
 * BookingScheduleControls / BookingScheduleControlsMobile with the returned
 * values and handlers.
 */
export function useBookingSchedule({
    availability,
    serverTimezone,
    requestTimezoneFromUrl,
    isReschedule = false,
    originalEventId,
    bookingDetails,
    actionPath,
    fetcherKey,
    enableMemberSelection = false,
}: UseBookingScheduleInput) {
    const fetcher = useFetcher<BookingActionData>(
        fetcherKey ? { key: fetcherKey } : undefined,
    );

    const [isConfirmationPhase, setIsConfirmationPhase] = useState(false);
    const [selectedTimezone, setSelectedTimezone] = useState(serverTimezone);
    const [browserTimezone, setBrowserTimezone] = useState<string | null>(null);
    useTimezoneSync(selectedTimezone);

    const availableTimezones = useMemo(() => {
        if (!availability?.freeDaysByDuration.length)
            return ['Pacific/Auckland'];
        const firstDuration = availability.freeDaysByDuration[0];
        const base = firstDuration?.freeDays.map((tz) => tz.timezone) || [
            'Pacific/Auckland',
        ];
        const extra: string[] = [];
        if (
            selectedTimezone &&
            !VALID_TIMEZONES.has(selectedTimezone) &&
            !base.includes(selectedTimezone)
        ) {
            extra.push(selectedTimezone);
        }
        if (
            browserTimezone &&
            !VALID_TIMEZONES.has(browserTimezone) &&
            !base.includes(browserTimezone) &&
            !extra.includes(browserTimezone)
        ) {
            extra.push(browserTimezone);
        }
        return extra.length ? [...base, ...extra] : base;
    }, [availability, selectedTimezone, browserTimezone]);

    useEffect(() => {
        setBrowserTimezone(getRawBrowserTimezone());
    }, []);

    const timezoneName = selectedTimezone;

    const isMutating =
        fetcher.state === 'submitting' || fetcher.state === 'loading';

    // Calculate initial duration from available data (synchronously for SSR)
    const initialDuration = useMemo(() => {
        if (!availability?.freeDaysByDuration.length) return 30;
        return availability.freeDaysByDuration[0]?.duration || 30;
    }, [availability]);

    const [selectedDuration, setSelectedDuration] =
        useState<number>(initialDuration);

    // Round-robin member preference. `null` = "No preference (first available)",
    // preserving the auto-assign behaviour; a member id filters availability to
    // that person's slots (BC-4603).
    const [selectedMemberId, setSelectedMemberId] = useState<number | null>(
        null,
    );

    // selectedDuration is seeded once (defaulting to 30 when availability hasn't
    // loaded yet). In the modal, availability arrives asynchronously and may not
    // offer a 30-minute option, which would leave freeDays empty. Re-sync to the
    // first available duration once the loaded data no longer contains the
    // current selection.
    useEffect(() => {
        if (!availability?.freeDaysByDuration.length) return;
        const available = availability.freeDaysByDuration.some(
            (d) => d.duration === selectedDuration,
        );
        if (!available) {
            setSelectedDuration(initialDuration);
        }
    }, [availability, selectedDuration, initialDuration]);

    // Get free days for the selected duration and timezone.
    // For unsupported timezones, convert from the first available timezone.
    const currentDurationData = useMemo(() => {
        if (!availability) return null;
        const durationGroup = availability.freeDaysByDuration.find(
            (d) => d.duration === selectedDuration,
        );
        const timezoneGroup = durationGroup?.freeDays.find(
            (tz) => tz.timezone === selectedTimezone,
        );
        if (timezoneGroup) return timezoneGroup;
        const firstTimezoneGroup = durationGroup?.freeDays[0];
        if (!firstTimezoneGroup?.days?.length) return null;
        const convertedDays = convertFreeDaysToTimezone(
            firstTimezoneGroup.days,
            selectedTimezone,
        );
        return { timezone: selectedTimezone, days: convertedDays };
    }, [availability, selectedDuration, selectedTimezone]);

    // When a specific round-robin member is chosen, restrict each day to slots
    // that member can take, dropping days left with no slots. This is the single
    // filtering point: the calendar, time dropdown, initial-slot selection and
    // submit all derive from `freeDays`, so they become member-aware here.
    const freeDays = useMemo(() => {
        const days = currentDurationData?.days || [];
        if (selectedMemberId == null) return days;
        return days
            .map((day) => ({
                ...day,
                freeSlots: day.freeSlots.filter((slot) =>
                    slot.availableMembers.some(
                        (member) => member.memberId === selectedMemberId,
                    ),
                ),
            }))
            .filter((day) => day.freeSlots.length > 0);
    }, [currentDurationData, selectedMemberId]);

    // Calculate initial selected timestamps synchronously for SSR
    const initialTimestamps = useMemo(() => {
        if (!freeDays.length) {
            return { dateTimestamp: null, slotTimestamp: null };
        }

        const firstDay = freeDays[0];
        if (!firstDay || !firstDay.freeSlots.length) {
            return { dateTimestamp: null, slotTimestamp: null };
        }

        const dateTimestamp = dayjs
            .tz(firstDay.date, timezoneName)
            .startOf('day')
            .valueOf();
        const slotTimestamp = firstDay.freeSlots[0]?.timestamp || null;

        return { dateTimestamp, slotTimestamp };
    }, [freeDays, timezoneName]);

    // State for selected date and time slot (initialized with computed values for SSR)
    const [selectedDateTimestamp, setSelectedDateTimestamp] = useState<
        number | null
    >(initialTimestamps.dateTimestamp);
    const [selectedSlotTimestamp, setSelectedSlotTimestamp] = useState<
        number | null
    >(initialTimestamps.slotTimestamp);

    // Handle duration change - check if current slot is still available, otherwise find next available slot
    const handleDurationChange = useCallback((newDuration: number) => {
        setSelectedDuration(newDuration);
    }, []);

    // When duration or freeDays change, check if current slot is still available
    useEffect(() => {
        // Don't check if booking was successful - preserve the selected time
        // Also don't check when in reschedule mode - user has selected a new time
        if (fetcher.data?.ok || isReschedule) {
            return;
        }

        // If no slot is selected, use initial timestamps
        if (!selectedSlotTimestamp) {
            setSelectedDateTimestamp(initialTimestamps.dateTimestamp);
            setSelectedSlotTimestamp(initialTimestamps.slotTimestamp);
            return;
        }

        // Check if current slot is still available in the new freeDays
        const currentSlotStillAvailable = freeDays.some((day) =>
            day.freeSlots.some(
                (slot) => slot.timestamp === selectedSlotTimestamp,
            ),
        );

        if (currentSlotStillAvailable) {
            // Current slot is still available, keep it
            return;
        }

        // Current slot is no longer available, find the next available slot
        // First, try to find a slot on the same date
        if (selectedDateTimestamp) {
            const selectedDateKey = dayjs(selectedDateTimestamp)
                .tz(timezoneName)
                .format('YYYY-MM-DD');
            const sameDay = freeDays.find((d) => d.date === selectedDateKey);
            const firstSlotOnSameDay = sameDay?.freeSlots[0];
            if (firstSlotOnSameDay) {
                // Found a slot on the same date, use it
                setSelectedSlotTimestamp(firstSlotOnSameDay.timestamp);
                return;
            }
        }

        // No slot available on the same date, find the next available date
        if (freeDays.length > 0) {
            const nextDay = freeDays[0];
            const firstSlotOnNextDay = nextDay?.freeSlots[0];
            if (firstSlotOnNextDay) {
                const dateTimestamp = dayjs
                    .tz(nextDay.date, timezoneName)
                    .startOf('day')
                    .valueOf();
                setSelectedDateTimestamp(dateTimestamp);
                setSelectedSlotTimestamp(firstSlotOnNextDay.timestamp);
                return;
            }
        }

        // No available slots at all
        setSelectedDateTimestamp(null);
        setSelectedSlotTimestamp(null);
    }, [
        selectedDuration,
        freeDays,
        selectedSlotTimestamp,
        selectedDateTimestamp,
        timezoneName,
        fetcher.data,
        isReschedule,
        initialTimestamps,
    ]);

    // Get free slots for the selected day
    const freeSlotsForSelectedDay = useMemo(() => {
        if (selectedDateTimestamp === null) {
            return [];
        }

        const selectedDateKey = dayjs(selectedDateTimestamp)
            .tz(timezoneName)
            .format('YYYY-MM-DD');

        const day = freeDays.find((d) => d.date === selectedDateKey);
        return day?.freeSlots || [];
    }, [freeDays, selectedDateTimestamp, timezoneName]);

    const freeHoursForSelectedDay = useMemo(
        () => freeSlotsForSelectedDay.map((slot) => slot.label),
        [freeSlotsForSelectedDay],
    );

    // Handle calendar date selection
    const handleCalendarSelect = useCallback(
        (timestamp: number | null | undefined) => {
            if (!Number.isFinite(timestamp ?? Number.NaN)) {
                setSelectedDateTimestamp(null);
                setSelectedSlotTimestamp(null);
                return;
            }

            setSelectedDateTimestamp(timestamp as number);

            // Auto-select first slot for this date
            const dateKey = dayjs(timestamp)
                .tz(timezoneName)
                .format('YYYY-MM-DD');
            const day = freeDays.find((d) => d.date === dateKey);
            const firstSlot = day?.freeSlots[0];
            setSelectedSlotTimestamp(firstSlot?.timestamp || null);
        },
        [freeDays, timezoneName],
    );

    // Handle timezone change
    const handleTimezoneChange = useCallback(
        (newTimezone: string) => {
            setSelectedTimezone(newTimezone);

            if (selectedSlotTimestamp) {
                const newLocalDate = dayjs(selectedSlotTimestamp)
                    .tz(newTimezone)
                    .format('YYYY-MM-DD');

                const durationGroup = availability?.freeDaysByDuration.find(
                    (d) => d.duration === selectedDuration,
                );
                let timezoneGroup = durationGroup?.freeDays.find(
                    (tz) => tz.timezone === newTimezone,
                );
                if (!timezoneGroup && durationGroup?.freeDays[0]) {
                    const firstGroup = durationGroup.freeDays[0];
                    const convertedDays = convertFreeDaysToTimezone(
                        firstGroup.days,
                        newTimezone,
                    );
                    timezoneGroup = {
                        timezone: newTimezone,
                        days: convertedDays,
                    };
                }
                const availableDay = timezoneGroup?.days.find(
                    (d) => d.date === newLocalDate,
                );

                if (availableDay && availableDay.freeSlots.length > 0) {
                    const newDateTimestamp = dayjs
                        .tz(newLocalDate, newTimezone)
                        .startOf('day')
                        .valueOf();
                    setSelectedDateTimestamp(newDateTimestamp);

                    const slotStillAvailable = availableDay.freeSlots.find(
                        (s) => s.timestamp === selectedSlotTimestamp,
                    );
                    setSelectedSlotTimestamp(
                        slotStillAvailable
                            ? selectedSlotTimestamp
                            : availableDay.freeSlots[0]?.timestamp || 0,
                    );
                } else {
                    const firstDay = timezoneGroup?.days?.[0];
                    if (firstDay && firstDay.freeSlots?.[0]) {
                        setSelectedDateTimestamp(
                            dayjs
                                .tz(firstDay.date, newTimezone)
                                .startOf('day')
                                .valueOf(),
                        );
                        setSelectedSlotTimestamp(
                            firstDay.freeSlots[0].timestamp,
                        );
                    }
                }
            }
        },
        [availability, selectedDuration, selectedSlotTimestamp],
    );

    // Browser timezone auto-detection: use handleTimezoneChange so both selectedTimezone
    // and selectedDateTimestamp are updated. Direct setSelectedTimezone would leave
    // selectedDateTimestamp as midnight in the old timezone, producing a wrong date key
    // in freeSlotsForSelectedDay when cookie/server tz differs from browser (e.g. 18h shift).
    useEffect(() => {
        if (requestTimezoneFromUrl) return;
        const tz = getRawBrowserTimezone();
        if (serverTimezone !== tz && selectedTimezone !== tz) {
            handleTimezoneChange(tz);
        }
    }, [
        requestTimezoneFromUrl,
        serverTimezone,
        selectedTimezone,
        handleTimezoneChange,
    ]);

    // Handle time slot selection
    const handleTimeSelect = useCallback(
        (timestamp: number) => {
            if (isMutating) return;
            if (!Number.isFinite(timestamp)) {
                setSelectedSlotTimestamp(null);
                return;
            }
            setSelectedSlotTimestamp(timestamp);
        },
        [isMutating],
    );

    // Time selection handler
    const handleTimeSelectWithAssignment = useCallback(
        (timestamp: number) => {
            handleTimeSelect(timestamp);
        },
        [handleTimeSelect],
    );

    const handleSubmit = useCallback(
        ({
            clientName,
            clientEmail,
            clientCompany,
        }: {
            clientName: string;
            clientEmail: string;
            clientCompany: string;
        }) => {
            if (!selectedSlotTimestamp || !availability) {
                return;
            }

            const form = new FormData();
            form.set('intent', 'book');
            form.set('sessionId', String(availability.sessionID));
            form.set(
                'startTime',
                dayjs(selectedSlotTimestamp)
                    .tz(timezoneName)
                    .format('YYYY-MM-DDTHH:mm:ssZ'),
            );
            form.set('duration', String(selectedDuration));

            // For reschedule, use the original organizer; otherwise select from available members
            if (isReschedule && bookingDetails?.organizerId) {
                form.set(
                    'organizerMemberId',
                    String(bookingDetails.organizerId),
                );
            } else {
                // Get the selected slot to find available member
                const selectedSlot = freeSlotsForSelectedDay.find(
                    (slot) => slot.timestamp === selectedSlotTimestamp,
                );
                if (selectedSlot && selectedSlot.availableMembers.length > 0) {
                    // Honour an explicit round-robin choice; otherwise keep the
                    // first-available auto-assign for "No preference".
                    const chosen =
                        selectedMemberId != null
                            ? selectedSlot.availableMembers.find(
                                  (member) =>
                                      member.memberId === selectedMemberId,
                              )
                            : undefined;
                    const member = chosen ?? selectedSlot.availableMembers[0];
                    if (member) {
                        form.set('organizerMemberId', String(member.memberId));
                    }
                }
            }

            // Use booking details from reschedule if available, otherwise use form values
            form.set('clientEmail', bookingDetails?.clientEmail || clientEmail);
            form.set('clientName', bookingDetails?.clientName || clientName);
            form.set(
                'clientCompany',
                bookingDetails?.clientCompany || clientCompany,
            );
            form.set('timezoneName', timezoneName);

            // Include original event ID if rescheduling
            if (isReschedule && originalEventId) {
                form.set('originalEventId', originalEventId);
            }

            fetcher.submit(
                form,
                actionPath
                    ? { method: 'post', action: actionPath }
                    : { method: 'post' },
            );
        },
        [
            selectedSlotTimestamp,
            availability,
            timezoneName,
            selectedDuration,
            freeSlotsForSelectedDay,
            selectedMemberId,
            fetcher,
            isReschedule,
            originalEventId,
            bookingDetails,
            actionPath,
        ],
    );

    const bookingDisabled = !selectedSlotTimestamp || isMutating;

    const success = fetcher.data?.ok;
    const failure = fetcher.data && !fetcher.data.ok;
    const statusMessage = fetcher.data?.message;
    const bookingIsReschedule = fetcher.data?.isReschedule ?? false;
    const bookingEventId = fetcher.data?.eventId;
    const clientDirectorEmail = fetcher.data?.clientDirectorEmail;
    const showSpamHint = clientDirectorEmail
        ? clientDirectorEmail.toLowerCase().includes('@hotmail.')
        : false;

    const selectedSlotLabel = selectedSlotTimestamp
        ? dayjs(selectedSlotTimestamp)
              .tz(timezoneName)
              .format('dddd, D MMMM • h:mm A')
        : 'Select a time';

    // Extract durations - prefer the dedicated durations field if available, otherwise use freeDaysByDuration
    const durations = useMemo(() => {
        if (!availability) return [];
        return availability.durations && availability.durations.length > 0
            ? availability.durations
                  .filter((d) => d.Checked === true)
                  .map((d) => d.Duration)
            : availability.freeDaysByDuration.map((d) => d.duration) || [];
    }, [availability]);

    const professional = useMemo(
        () =>
            availability?.professional
                ? {
                      id: availability.professional.ID || null,
                      title: availability.professional.Title || null,
                      companyLogo:
                          availability.professional.CompanyLogo || null,
                      logoBackgroundColor:
                          availability.professional.LogoBackgroundColor || null,
                      urlSegment: availability.professional.URLSegment || null,
                      phone: availability.professional.Phone || null,
                      website: availability.professional.Website || null,
                      primaryAddress:
                          availability.professional.PrimaryAddress || null,
                      teaser: availability.professional.Teaser || null,
                      libraryThumb: availability.professional.LibraryThumb
                          ? {
                                thumbImageFile:
                                    availability.professional.LibraryThumb
                                        .ThumbImageFile?.PhotoTileDraggable ||
                                    null,
                            }
                          : null,
                      categories:
                          availability.professional.Categories?.map(
                              (cat) => cat.PrettyTitle || '',
                          ).filter(Boolean) || null,
                  }
                : null,
        [availability],
    );

    // Get consulted member for one-on-one sessions
    const consultedMember = useMemo(
        () =>
            availability?.meetingType === 'one-on-one' &&
            availability.oneOnOneTeamMember
                ? {
                      id: availability.oneOnOneTeamMember.ID || 0,
                      memberId: availability.oneOnOneTeamMember.MemberID || 0,
                      name: availability.oneOnOneTeamMember.Name || '',
                      email: availability.oneOnOneTeamMember.Email || '',
                      phone: availability.oneOnOneTeamMember.Phone || null,
                      calendarEmail:
                          availability.oneOnOneTeamMember
                              .CalendarConnection?.[0]?.email ||
                          availability.oneOnOneTeamMember.Email ||
                          '',
                      calendarProvider:
                          availability.oneOnOneTeamMember
                              .CalendarConnection?.[0]?.provider || null,
                      avatarUrl:
                          availability.oneOnOneTeamMember.MeetingImage
                              ?.Avatar ||
                          availability.oneOnOneTeamMember.MeetingImage?.Logo ||
                          availability.oneOnOneTeamMember.MemberImage?.Avatar ||
                          availability.oneOnOneTeamMember.MemberImage?.Logo ||
                          null,
                      professionalName:
                          availability.oneOnOneTeamMember.Professional?.Title ||
                          null,
                      professionalLogoUrl:
                          availability.oneOnOneTeamMember.Professional
                              ?.CompanyLogo || null,
                  }
                : null,
        [availability],
    );

    const meetingType =
        (availability?.meetingType as 'one-on-one' | 'random' | null) ||
        'one-on-one';

    // Round-robin team roster, as dropdown options (value = member id).
    const memberOptions = useMemo(() => {
        if (meetingType !== 'random') return [];
        return (availability?.teamMembers ?? [])
            .filter((member) => member.MemberID != null)
            .map((member) => ({
                value: String(member.MemberID),
                label: member.Name || 'Team member',
            }));
    }, [availability, meetingType]);

    // When member selection is enabled, round-robin sessions default to the
    // first team member (no "no preference"). Switching sessions resets the pick
    // to the new session's first member — keeping the prior choice would filter
    // the new session's availability by a person who may not be on it, hiding
    // valid slots or showing a misleading empty state. Reseed too when the
    // current pick simply isn't in this roster. One-on-one sessions, and every
    // pooled flow that leaves member selection off, clear the pick.
    const sessionId = availability?.sessionID ?? null;
    const memberSeedSessionRef = useRef<number | null>(null);
    useEffect(() => {
        if (!enableMemberSelection || meetingType !== 'random') {
            if (selectedMemberId !== null) setSelectedMemberId(null);
            memberSeedSessionRef.current = sessionId;
            return;
        }
        const memberIds = memberOptions.map((option) => Number(option.value));
        if (memberIds.length === 0) return;
        const sessionChanged = memberSeedSessionRef.current !== sessionId;
        memberSeedSessionRef.current = sessionId;
        if (
            sessionChanged ||
            selectedMemberId == null ||
            !memberIds.includes(selectedMemberId)
        ) {
            setSelectedMemberId(memberIds[0] ?? null);
        }
    }, [
        enableMemberSelection,
        meetingType,
        memberOptions,
        sessionId,
        selectedMemberId,
    ]);

    // Display info for the chosen round-robin member, so the consultant tile can
    // swap to their photo/name.
    const selectedMember = useMemo(() => {
        if (selectedMemberId == null) return null;
        const member = availability?.teamMembers.find(
            (candidate) => candidate.MemberID === selectedMemberId,
        );
        if (!member) return null;
        return {
            memberId: member.MemberID || 0,
            name: member.Name || '',
            avatarUrl:
                member.MemberImage?.LargeAvatar ||
                member.MemberImage?.Logo ||
                null,
            professionalName: member.Professional?.Title || null,
        };
    }, [availability, selectedMemberId]);

    // Session-level cover image takes precedence over member/professional images
    const sessionImageUrl =
        availability?.meetingImage?.Avatar ||
        availability?.meetingImage?.Logo ||
        null;

    return {
        fetcher,
        // timezone
        timezoneName,
        selectedTimezone,
        availableTimezones,
        handleTimezoneChange,
        // duration
        durations,
        selectedDuration,
        setSelectedDuration,
        handleDurationChange,
        isDurationDisabled: !durations.length,
        // days / slots
        freeDays,
        freeHoursForSelectedDay,
        selectedDateTimestamp,
        handleCalendarSelect,
        selectedSlotTimestamp,
        handleTimeSelectWithAssignment,
        selectedSlotLabel,
        // session meta
        meetingType,
        professional,
        consultedMember,
        // round-robin member selection
        memberOptions,
        selectedMemberId,
        setSelectedMemberId,
        selectedMember,
        sessionImageUrl,
        // submit lifecycle
        handleSubmit,
        bookingDisabled,
        submissionState: fetcher.state,
        success,
        failure,
        statusMessage,
        bookingIsReschedule,
        bookingEventId,
        showSpamHint,
        // confirmation phase (desktop)
        isConfirmationPhase,
        setIsConfirmationPhase,
    };
}
